import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import errorMessages from '../../../common/constants/errorMessages';
import { AuthService } from '../../../features/auth/services/auth.service';
import ResponseCode from '../../../common/constants/responseCodes';

/** Shape thrown by this interceptor so callers can read `e.error.error` from the API. */
export interface ApiError {
  status: number;
  error: any;
  message: string;
}

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(private authService: AuthService) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === ResponseCode.UNAUTHORIZED.code) {
          // Token expirado. Intentar refresh y reintentar la solicitud original.
          return this.handle401Error(request, next);
        }

        // Keep the parsed body so callers can read the API's own message via e.error.error
        const apiError: ApiError = {
          status: error.status,
          error: error.error,
          message: this.getErrorMessage(error),
        };
        return throwError(() => apiError);
      }),
    );
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return new Observable((observer) => {
      // Every path below must settle the observer. Anything that throws here would
      // leave the caller awaiting a request that never completes.
      const fail = (reason: unknown) => {
        // Only end a session that exists. An account that signed up but has not been
        // approved yet legitimately has no usable token, so its 401s are not an expired
        // session -- signing it out would drop the applicant out of registration.
        if (this.authService.account) {
          this.authService
            .logOut()
            .catch((logoutError) =>
              console.error('Failed to sign out after an expired session', logoutError),
            );
        }
        observer.error(reason);
      };

      this.authService.refreshToken().then((newToken) => {
        if (newToken) {
          // Clonar request con el nuevo token
          const clonedRequest = request.clone({
            setHeaders: {
              Authorization: `Bearer ${newToken}`,
            },
          });

          // Reintentar la solicitud
          next.handle(clonedRequest).subscribe({
            next: (event) => observer.next(event),
            error: (err) => observer.error(err),
            complete: () => observer.complete(),
          });
        } else {
          // No se pudo renovar token, cerrar sesión
          fail('Sesión expirada. Por favor, inicie sesión nuevamente.');
        }
      }).catch((refreshError) => fail(refreshError));
    });
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    if (error.status === ResponseCode.CONTENT_TOO_LARGE.code) {
      return errorMessages[`${ResponseCode.CONTENT_TOO_LARGE.code}`];
    }

    if (error.error?.error?.message) {
      return (
        errorMessages[error.error.error.message] ||
        ResponseCode.SERVER_ERROR.message
      );
    }

    return errorMessages[error.message] || ResponseCode.SERVER_ERROR.message;
  }
}
